import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  Fab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add,
  Visibility,
  LocalShipping,
  CheckCircle,
  QrCodeScanner,
  Store,
  Delete,
  Download
} from '@mui/icons-material';
import { useAppData } from '../../context/AppDataContext';
import { getTransferList, deleteTransfer, downloadTransferReport } from '../../services/api';
import { useFlashMessage } from '../../hooks/useFlashMessage';
import { FEATURE_PERMISSIONS } from '../../utils/frontendPermissions';

// Mock data for transfer orders
const mockTransferOrders = [
  {
    id: 'TRF-2024-001',
    date: '2024-01-15',
    fromLocation: 'Main Warehouse',
    toLocation: 'Store A',
    status: 'completed',
    totalItems: 15,
    requestedBy: 'Store Manager A',
    deliveredBy: 'John Transport',
    estimatedDelivery: '2024-01-16',
    actualDelivery: '2024-01-16'
  },
  {
    id: 'TRF-2024-002',
    date: '2024-01-14',
    fromLocation: 'Main Warehouse',
    toLocation: 'Store B',
    status: 'confirmed',
    totalItems: 8,
    requestedBy: 'Store Manager B',
    deliveredBy: 'Mike Transport',
    estimatedDelivery: '2024-01-15',
    actualDelivery: null
  },
  {
    id: 'TRF-2024-003',
    date: '2024-01-14',
    fromLocation: 'Main Warehouse',
    toLocation: 'Store C',
    status: 'prepared',
    totalItems: 22,
    requestedBy: 'Store Manager C',
    deliveredBy: null,
    estimatedDelivery: '2024-01-16',
    actualDelivery: null
  },
  {
    id: 'TRF-2024-004',
    date: '2024-01-13',
    fromLocation: 'Main Warehouse',
    toLocation: 'Store D',
    status: 'draft',
    totalItems: 5,
    requestedBy: 'Store Manager D',
    deliveredBy: null,
    estimatedDelivery: '2024-01-17',
    actualDelivery: null
  }
];

const getStatusColor = (status) => {
  switch (status) {
    case 'completed':
    case 'completed':
    case 'done':
      return { color: 'success', label: 'completed' };
    case 'confirmed':
      return { color: 'warning', label: 'Confirmed' };
    case 'confirmed':
    case 'in_progress':
      return { color: 'info', label: 'In Transit' };
    case 'prepared':
    case 'pending':
      return { color: 'warning', label: 'Prepared' };
    case 'draft':
      return { color: 'default', label: 'Draft' };
    default:
      return { color: 'default', label: 'Unknown' };
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'completed':
      return CheckCircle;
    case 'confirmed':
      return CheckCircle;
    case 'confirmed':
      return LocalShipping;
    case 'prepared':
      return QrCodeScanner;
    default:
      return Add;
  }
};

const COLUMN_WIDTHS = {
  orderId: '9%',
  date: '9%',
  from: '12%',
  to: '12%',
  status: '10%',
  items: '7%',
  requestedBy: '8%',
  locationStatus: '9%',
  processTrail: '9%',
};

const ACTIONS_COLUMN_WIDTH = '18%';
const DOWNLOADABLE_STATUSES = ['verifiedserials', 'confirmed', 'completed'];

const mapTransferToOrder = (transfer) => ({
  ...transfer,
  id: transfer._id,
  transferCode: transfer.transferCode,
  date: new Date(transfer.createdAt).toISOString().split('T')[0],
  fromLocation: transfer.fromBranchName,
  toLocation: transfer.toBranchName,
  status: transfer.status,
  totalItems: transfer.items?.length || 0,
  requestedBy: transfer.createdByUserName,
  deliveredBy: transfer.receivedByUserName || null,
  estimatedDelivery: '',
  actualDelivery: transfer.completedAt ? new Date(transfer.completedAt).toISOString().split('T')[0] : null,
  locationStatus: transfer.locationStatus || transfer.status || '',
  createdByUserName: transfer.createdByUserName || '',
  warehouseConfirmedByUserName: transfer.warehouseConfirmedByUserName || '',
  storeReceivedByUserName: transfer.storeReceivedByUserName || ''
});

const InventoryTransferList = () => {
  const navigate = useNavigate();
  const { getStoreList, frontendPermissions, permissions } = useAppData();
  const { showError, showSuccess } = useFlashMessage();
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Confirmed orders waiting for final processing
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [loadingConfirmed, setLoadingConfirmed] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(10);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('inProgress');
  const [downloadingId, setDownloadingId] = useState(null);

  // Delete dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteConfirmationCode, setDeleteConfirmationCode] = useState('');
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Load orders from API
  const loadOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare filter params
      const params = {
        page: page,
        limit: limit
      };

      // Add status filter only if not "all"
      if (selectedStatus && selectedStatus !== 'all') {
        params.status = selectedStatus;
      }

      // Call API
      const response = await getTransferList(params);

      // Handle response
      if (response && response.data) {
        // Map API response to our order structure
        const mappedOrders = response.data.map(mapTransferToOrder);

        setOrders(mappedOrders);

        // Update pagination info
        setTotalItems(response.total || 0);
        setTotalPages(Math.ceil((response.total || 0) / limit));
      } else if (response && Array.isArray(response)) {
        // Handle case where API returns array directly
        const mappedOrders = response.map(mapTransferToOrder);

        setOrders(mappedOrders);
        setTotalItems(mappedOrders.length);
        setTotalPages(1);
      } else {
        setOrders([]);
        setTotalItems(0);
        setTotalPages(1);
      }
    } catch (err) {
      setError('Failed to load transfer orders from API.');
      showError('Failed to load transfer orders. Please try again.');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Load confirmed orders waiting for final processing
  const loadConfirmedOrders = async () => {
    setLoadingConfirmed(true);
    try {
      const params = {
        page: 1,
        limit: 10,
        status: 'confirmed'
      };

      const response = await getTransferList(params);

      if (response && response.data) {
        const mappedOrders = response.data.map(mapTransferToOrder);

        setConfirmedOrders(mappedOrders);
      } else {
        setConfirmedOrders([]);
      }
    } catch (err) {
      console.error('Failed to load confirmed orders:', err);
      setConfirmedOrders([]);
    } finally {
      setLoadingConfirmed(false);
    }
  };

  useEffect(() => {
    loadOrders();
    loadConfirmedOrders(); // Load confirmed orders on mount
  }, [selectedStatus, page]);

  useEffect(() => {
    // No client-side filtering needed anymore
    setFilteredOrders(orders);
  }, [orders]);

  const handleViewDetail = (order) => {
    navigate(`/inventory-transfer/${order.id}`);
  };

  const handleCreateNew = () => {
    navigate('/inventory-transfer/create');
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/import-template.csv';
    link.download = 'import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isDownloadableStatus = (status = '') =>
    DOWNLOADABLE_STATUSES.includes((status || '').toLowerCase());

  const handleDownloadReportClick = async (order, e) => {
    e.stopPropagation();
    if (!order?.id) return;
    setDownloadingId(order.id);
    try {
      const { blob, filename } = await downloadTransferReport(order.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('Report downloaded successfully.');
    } catch (err) {
      showError(err.message || 'Failed to download report.');
    } finally {
      setDownloadingId(null);
    }
  };

  // Generate random 8-digit confirmation code
  const generateConfirmationCode = () => {
    return Math.floor(10000000 + Math.random() * 90000000).toString();
  };

  const handleDeleteClick = (order, e) => {
    e.stopPropagation();
    setOrderToDelete(order);
    const confirmationCode = generateConfirmationCode();
    setDeleteConfirmationCode(confirmationCode);
    setDeleteConfirmationInput('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
    setDeleteConfirmationCode('');
    setDeleteConfirmationInput('');
  };

  const handleDeleteConfirm = async () => {
    if (!orderToDelete) return;
    
    // Validate that the entered confirmation code matches
    if (deleteConfirmationInput !== deleteConfirmationCode) {
      showError('Confirmation code does not match. Please enter the correct code.');
      return;
    }

    setIsDeleting(true);
    
    try {
      // Call delete API - will throw error if status is not 200
      await deleteTransfer(orderToDelete.id);
      
      // Only show success message if API returns 200 (no error thrown)
      showSuccess('Transfer order deleted successfully.');
      
      // Reload orders after successful deletion
      await loadOrders();
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      setDeleteConfirmationCode('');
      setDeleteConfirmationInput('');
    } catch (err) {
      // API returned error (not 200) - show error message
      const errorMessage = err.message || 'Failed to delete transfer order. Please try again.';
      showError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusStats = () => {
    const stats = {};
    mockTransferOrders.forEach(order => {
      stats[order.status] = (stats[order.status] || 0) + 1;
    });
    return stats;
  };

  const stats = getStatusStats();
  const canCreateTransfer =
    permissions.includes('admin') ||
    frontendPermissions.includes(
      FEATURE_PERMISSIONS.INVENTORY_TRANSFER.CREATE
    );
  
  const canDeleteTransfer =
    permissions.includes('admin') ||
    frontendPermissions.includes(
      FEATURE_PERMISSIONS.INVENTORY_TRANSFER.CREATE
    );

  const displayedOrders = filteredOrders.filter(order => {
    const normalizedStatus = (order.status || '').toLowerCase();
    if (activeTab === 'completed') {
      return normalizedStatus === 'completed';
    }
    return normalizedStatus !== 'completed';
  });

  return (
    <Box sx={{ px: { xs: 0, sm: 1 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 3, sm: 6 } }}>
          <Typography variant="h4" sx={{
            fontWeight: 700,
            color: '#1e293b',
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem' }
          }}>
            Inventory Transfer Management
          </Typography>
          <Typography variant="body1" sx={{
            color: '#64748b',
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}>
            Manage warehouse to store transfers and track delivery status
          </Typography>
        </Box>

        {/* Confirmed Orders - Waiting for Final Processing */}
        {confirmedOrders.length > 0 && (
          <Card sx={{ mb: { xs: 3, sm: 4 }, border: '2px solid #f59e0b', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)' }}>
            <CardContent>
              <Box className="flex items-center gap-2 mb-3">
                <CheckCircle sx={{ color: '#f59e0b', fontSize: 28 }} />
                <Typography variant="h6" className="font-semibold text-orange-600">
                  Orders Awaiting Final Processing
                </Typography>
                <Chip
                  label={confirmedOrders.length}
                  size="small"
                  sx={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />
              </Box>
              <Typography variant="body2" className="text-gray-600 mb-3">
                These confirmed transfers are ready for the final step. Click to view details and complete the process.
              </Typography>

              {loadingConfirmed ? (
                <Box className="flex justify-center py-4">
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {confirmedOrders.map((order) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: '1px solid #fbbf24',
                          backgroundColor: '#fffbeb',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                            transform: 'translateY(-2px)',
                            borderColor: '#f59e0b'
                          }
                        }}
                        onClick={() => handleViewDetail(order)}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box className="flex justify-between items-start mb-2">
                            <Typography variant="subtitle2" className="font-bold text-blue-600">
                              {order.transferCode || order.id}
                            </Typography>
                            <Chip
                              label="CONFIRMED"
                              size="small"
                              sx={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                          </Box>

                          <Box className="space-y-1">
                            <Box className="flex items-center gap-1">
                              <Store sx={{ fontSize: 14, color: '#6b7280' }} />
                              <Typography variant="caption" className="text-gray-600">
                                {order.fromLocation}
                              </Typography>
                            </Box>
                            <Typography variant="caption" className="text-gray-400 px-4">→</Typography>
                            <Box className="flex items-center gap-1">
                              <Store sx={{ fontSize: 14, color: '#6b7280' }} />
                              <Typography variant="caption" className="text-gray-600">
                                {order.toLocation}
                              </Typography>
                            </Box>
                          </Box>

                          <Box className="flex justify-between items-center mt-2 pt-2 border-t border-orange-200">
                            <Typography variant="caption" className="text-gray-500">
                              {order.totalItems} items
                            </Typography>
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<Visibility sx={{ fontSize: 14 }} />}
                              sx={{
                                fontSize: '0.7rem',
                                color: '#f59e0b',
                                p: 0.5,
                                minWidth: 'auto'
                              }}
                            >
                              View
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        )}

        {/* Compact Filter Section */}
        <Card sx={{ mb: { xs: 3, sm: 6 } }}>
          <CardContent sx={{ py: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
              {/* Status Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">In Transit</MenuItem>
                    <MenuItem value="completed">Delivered</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12} sm={6} md={10}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownloadTemplate}
                    sx={{ minHeight: 32 }}
                  >
                    Download Import Template CSV
                  </Button>
                  {canCreateTransfer && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Add />}
                      onClick={handleCreateNew}
                      className="bg-blue-600 hover:bg-blue-700"
                      sx={{
                        minHeight: 32,
                        px: 2
                      }}
                    >
                      New
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Transfer Orders Table */}
        <Card>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            aria-label="Transfer status tabs"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="inProgress" label="Active Transfers" />
            <Tab value="completed" label="Completed Transfers" />
          </Tabs>
          <TableContainer component={Paper}>
            <Table>
              <TableHead className="bg-gray-50">
                <TableRow>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.orderId }}>Order ID</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.date }}>Date</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.from }}>From</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.to }}>To</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.status }}>Status</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.items }}>Items</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.requestedBy }}>Requested By</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.locationStatus }}>Location Status</TableCell>
                  <TableCell className="font-semibold" sx={{ width: COLUMN_WIDTHS.processTrail }}>Process Trail</TableCell>
                  <TableCell className="font-semibold" align="right" sx={{ width: ACTIONS_COLUMN_WIDTH, pr: 12 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : displayedOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No transfer orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedOrders.map((order) => {
                    const statusInfo = getStatusColor(order.status);
                    const StatusIcon = getStatusIcon(order.status);

                    return (
                      <TableRow
                        key={order.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewDetail(order)}
                      >
                        <TableCell className="font-medium text-blue-600" sx={{ width: COLUMN_WIDTHS.orderId }}>
                          {order.transferCode || order.id}
                        </TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.date }}>{order.date}</TableCell>
                        <TableCell className="text-green-600 font-medium" sx={{ width: COLUMN_WIDTHS.from }}>
                          {order.fromLocation}
                        </TableCell>
                        <TableCell className="text-purple-600 font-medium" sx={{ width: COLUMN_WIDTHS.to }}>
                          {order.toLocation}
                        </TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.status }}>
                          <Box className="flex items-center space-x-2">
                            <StatusIcon
                              className={`text-${statusInfo.color === 'success' ? 'green' :
                                statusInfo.color === 'info' ? 'blue' :
                                  statusInfo.color === 'warning' ? 'orange' : 'gray'}-500`}
                              fontSize="small"
                            />
                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.items }}>{order.totalItems}</TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.requestedBy }}>{order.requestedBy}</TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.locationStatus }}>
                          <Tooltip title={order.locationStatus || 'No status'}>
                            <Chip
                              label={order.locationStatus || '—'}
                              size="small"
                              sx={{
                                backgroundColor: '#e0f2fe',
                                color: '#0c4a6e',
                                fontWeight: 600,
                                textTransform: 'capitalize',
                                cursor: order.locationStatus ? 'pointer' : 'default'
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ width: COLUMN_WIDTHS.processTrail }}>
                          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                            {[
                              { value: order.createdByUserName, bg: '#facc15', color: '#3b2f00' },
                              { value: order.warehouseConfirmedByUserName, bg: '#3b82f6', color: '#eff6ff' },
                              { value: order.storeReceivedByUserName, bg: '#22c55e', color: '#052e16' },
                            ].map((tag, index) => (
                              <Chip
                                key={`${order.id}-tag-${index}`}
                                label={tag.value || '—'}
                                size="small"
                                sx={{
                                  backgroundColor: tag.bg,
                                  color: tag.color,
                                  fontWeight: 600,
                                  maxWidth: 140,
                                  minWidth: 80
                                }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: ACTIONS_COLUMN_WIDTH, textAlign: 'center' }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                            {canDeleteTransfer && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleDeleteClick(order, e)}
                                className="text-red-600"
                                title="Delete Transfer"
                              >
                                <Delete />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetail(order);
                              }}
                              className="text-blue-600"
                              title="View Transfer"
                            >
                              <Visibility />
                            </IconButton>
                            {isDownloadableStatus(order.status) && (
                              <Tooltip title="Download report">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleDownloadReportClick(order, e)}
                                    className="text-teal-600"
                                    disabled={downloadingId === order.id}
                                  >
                                    {downloadingId === order.id ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <Download />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {order.status === 'prepared' && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle start delivery
                                }}
                                className="text-green-600"
                                title="Start Delivery"
                              >
                                <LocalShipping />
                              </IconButton>
                            )}
                            {order.status === 'confirmed' && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle confirm delivery
                                }}
                                className="text-green-600"
                                title="Confirm Delivery"
                              >
                                <CheckCircle />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, gap: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Showing {orders.length} of {totalItems} transfer orders
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Card>

        {/* Process Flow Info */}
        <Card className="mt-6">
          <CardContent>
            <Typography variant="h6" className="font-semibold mb-4">
              Transfer Process Flow
            </Typography>
            <Box className="flex flex-wrap gap-4 items-center">
              <Box className="flex items-center space-x-2">
                <Box className="w-3 h-3 bg-gray-400 rounded-full"></Box>
                <Typography variant="body2">1. Create Transfer Order</Typography>
              </Box>
              <Typography variant="body2" className="text-gray-400">→</Typography>
              <Box className="flex items-center space-x-2">
                <Box className="w-3 h-3 bg-orange-400 rounded-full"></Box>
                <Typography variant="body2">2. Prepare Items (RFID Scan)</Typography>
              </Box>
              <Typography variant="body2" className="text-gray-400">→</Typography>
              <Box className="flex items-center space-x-2">
                <Box className="w-3 h-3 bg-blue-400 rounded-full"></Box>
                <Typography variant="body2">3. In Transit</Typography>
              </Box>
              <Typography variant="body2" className="text-gray-400">→</Typography>
              <Box className="flex items-center space-x-2">
                <Box className="w-3 h-3 bg-green-400 rounded-full"></Box>
                <Typography variant="body2">4. Delivered & Confirmed</Typography>
              </Box>
            </Box>
            <Typography variant="caption" className="text-gray-500 mt-2 block">
              All transfers require RFID scanning for security verification at both origin and destination
            </Typography>
          </CardContent>
        </Card>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" className="font-semibold text-red-600">
              Confirm Delete Transfer Order
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Are you sure you want to delete this transfer order? This action cannot be undone.
            </Typography>
            
            {orderToDelete && (
              <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Transfer Order: {orderToDelete.transferCode || orderToDelete.id}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  From: {orderToDelete.fromLocation} → To: {orderToDelete.toLocation}
                </Typography>
              </Box>
            )}

            {/* Confirmation Code */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Confirmation code:
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#2563eb',
                  fontFamily: 'monospace',
                  letterSpacing: 2
                }}
              >
                {deleteConfirmationCode}
              </Typography>
            </Box>

            <TextField
              autoFocus
              margin="dense"
              label="Enter Confirmation Code"
              type="text"
              fullWidth
              variant="outlined"
              value={deleteConfirmationInput}
              onChange={(e) => setDeleteConfirmationInput(e.target.value)}
              placeholder="Enter the 8-digit code shown above"
              error={deleteConfirmationInput !== '' && deleteConfirmationInput !== deleteConfirmationCode}
              helperText={
                deleteConfirmationInput !== '' && deleteConfirmationInput !== deleteConfirmationCode
                  ? 'Confirmation code does not match'
                  : ''
              }
              inputProps={{
                maxLength: 8,
                pattern: '[0-9]*',
                inputMode: 'numeric'
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={
                isDeleting ||
                !deleteConfirmationInput ||
                deleteConfirmationInput !== deleteConfirmationCode
              }
              sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Floating Action Button */}
        {canCreateTransfer && (
          <Fab
            color="primary"
            aria-label="add transfer"
            onClick={handleCreateNew}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
                color: 'white',
              },
              '& .MuiSvgIcon-root': {
                color: 'white',
              }
            }}
          >
            <Add />
          </Fab>
        )}
      </Box>
  );
};

export default InventoryTransferList;